import { useState, useMemo } from "react";

const TODAY = () => new Date().toISOString().slice(0, 10);

function dateDiffDays(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.floor((d - now) / 86400000);
}

function fmtDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("he-IL", {
    day: "2-digit", month: "2-digit", year: "2-digit",
  });
}

export default function ClientNotesModal({ entries, onAdd, onUpdate, onDelete, onClose, clientName }) {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);

  const [form, setForm] = useState({
    description: "",
    reminder: "",
  });

  const notes = useMemo(() => entries.filter(e => e.type === "note").sort((a, b) => new Date(b.date) - new Date(a.date)), [entries]);

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!form.description.trim()) return;
    onAdd({
      type: "note",
      description: form.description.trim(),
      clientName: clientName || undefined,
      reminder: form.reminder || undefined,
      date: TODAY(),
    });
    setForm({ description: "", reminder: "" });
    setShowForm(false);
  };

  const startEdit = (entry) => {
    setEditId(entry.id);
    setEditForm({
      description: entry.description || "",
      reminder: entry.reminder || "",
    });
  };

  const saveEdit = (entry) => {
    if (!editForm.description.trim()) return;
    onUpdate(entry.id, {
      description: editForm.description.trim(),
      reminder: editForm.reminder || undefined,
    });
    setEditId(null);
    setEditForm(null);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 3000,
      background: "rgba(15,23,42,0.45)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      direction: "rtl", padding: 20,
    }}>
      <div style={{
        width: 520, maxWidth: "100%", maxHeight: "90vh",
        background: "#f8fafc", borderRadius: 16,
        display: "flex", flexDirection: "column",
        boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}>
        {/* ── Header ── */}
        <div style={{
          padding: "16px 20px", background: "linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)",
          color: "white",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          flexShrink: 0,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22 }}>📝</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 18 }}>הערות כלליות</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>{clientName}</div>
            </div>
          </div>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.3)",
            color: "white", width: 32, height: 32, borderRadius: 8,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 18,
          }}>✕</button>
        </div>

        {/* ── Add button / form ── */}
        <div style={{ padding: "16px 20px 0", flexShrink: 0 }}>
          {!showForm ? (
            <button
              onClick={() => setShowForm(true)}
              style={{
                width: "100%", padding: "12px",
                background: "white", color: "#4338ca", border: "1.5px dashed #c7d2fe", borderRadius: 10,
                cursor: "pointer", fontWeight: 700, fontSize: 14,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}
            ><span>➕</span> הוסף הערה חדשה</button>
          ) : (
            <form onSubmit={handleSubmit} style={{
              padding: "16px", display: "flex", flexDirection: "column", gap: 12,
              background: "white", border: "1px solid #e2e8f0", borderRadius: 12,
            }}>
              <label style={labelStyle}>
                <span style={labelText}>תוכן ההערה</span>
                <textarea placeholder="כתוב כאן..." required autoFocus
                  value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  style={{ ...inputStyle, resize: "vertical", minHeight: 80 }} />
              </label>

              <label style={{ ...labelStyle, maxWidth: 180 }}>
                <span style={labelText}>🔔 תזכורת (אופציונלי)</span>
                <input type="date" value={form.reminder}
                  onChange={e => setForm(f => ({ ...f, reminder: e.target.value }))} style={inputStyle} />
              </label>

              <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                <button type="submit" style={{
                  flex: 1, padding: "10px", background: "#4338ca", color: "white",
                  border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 700, fontSize: 13,
                }}>שמור הערה</button>
                <button type="button" onClick={() => setShowForm(false)} style={{
                  flex: "0 0 80px", padding: "10px", background: "white", color: "#64748b",
                  border: "1px solid #cbd5e1", borderRadius: 8, cursor: "pointer", fontSize: 13,
                }}>ביטול</button>
              </div>
            </form>
          )}
        </div>

        {/* ── Timeline ── */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
          {notes.length === 0 && !showForm && (
            <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 14, marginTop: 40 }}>
              <div style={{ fontSize: 40, marginBottom: 12, opacity: 0.5 }}>📝</div>
              אין הערות ללקוח זה
            </div>
          )}
          {notes.map(entry => {
            const isEditing = editId === entry.id;

            return (
              <div key={entry.id} style={{
                background: isEditing ? "#f0f9ff" : "white",
                borderRadius: 12, border: `1px solid ${isEditing ? "#7dd3fc" : "#e2e8f0"}`,
                padding: "14px", position: "relative",
              }}>
                {isEditing ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <textarea
                      value={editForm.description}
                      onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                      style={{ ...inputStyle, resize: "vertical", minHeight: 70 }}
                      autoFocus
                    />
                    <label style={{ ...labelStyle, maxWidth: 160 }}>
                      <span style={labelText}>🔔 תזכורת</span>
                      <input type="date" value={editForm.reminder}
                        onChange={e => setEditForm(f => ({ ...f, reminder: e.target.value }))}
                        style={{ ...inputStyle, fontSize: 12 }} />
                    </label>
                    <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
                      <button onClick={() => saveEdit(entry)} style={{
                        padding: "8px 16px", background: "#16a34a", color: "white",
                        border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 700, fontSize: 12,
                      }}>✓ שמור</button>
                      <button onClick={() => setEditId(null)} style={{
                        padding: "8px 16px", background: "white", color: "#64748b",
                        border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer", fontSize: 12,
                      }}>ביטול</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{fmtDate(entry.date)}</span>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => startEdit(entry)} style={{ ...iconBtn, color: "#3b82f6" }} title="ערוך">✏️</button>
                        <button onClick={() => onDelete(entry.id)} style={{ ...iconBtn, color: "#ef4444" }} title="מחק">🗑️</button>
                      </div>
                    </div>
                    <div style={{ fontSize: 14, color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
                      {entry.description}
                    </div>
                    {entry.reminder && (() => {
                      const diff = dateDiffDays(entry.reminder);
                      const due = diff !== null && diff <= 0;
                      return (
                        <div style={{ marginTop: 10, display: "inline-block", padding: "4px 8px", background: due ? "#fef2f2" : "#f1f5f9", borderRadius: 6 }}>
                          <span style={{ fontSize: 11, color: due ? "#dc2626" : "#64748b", fontWeight: due ? 700 : 500 }}>
                            🔔 תזכורת: {fmtDate(entry.reminder)}
                          </span>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Styles ──
const iconBtn = {
  background: "transparent", border: "none", cursor: "pointer",
  fontSize: 14, padding: 0, display: "flex", alignItems: "center", justifyContent: "center",
};

const labelStyle = { display: "flex", flexDirection: "column", gap: 4 };
const labelText = { fontSize: 11, fontWeight: 700, color: "#64748b", textAlign: "right" };

const inputStyle = {
  fontSize: 13, padding: "10px 12px", border: "1px solid #cbd5e1", borderRadius: 8,
  outline: "none", direction: "rtl", textAlign: "right", background: "#f8fafc",
  fontFamily: "inherit",
  boxSizing: "border-box",
  width: "100%"
};
